import React from 'react';
import Footer from '../components/Footer';

const Privacy: React.FC = () => {
  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: 'linear-gradient(to bottom, #0c0d1f, #1b1324)' }}
    >
      <div className="flex-1 px-6 md:px-12 lg:px-20 py-12">
        <div className="max-w-4xl mx-auto space-y-8 bg-white/5 border border-white/10 rounded-3xl p-8 backdrop-blur">
          <div>
            <p className="text-primary-200 text-sm uppercase tracking-wide mb-2">Privacy Policy</p>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">AdSmash Privacy Policy</h1>
            <p className="text-white/70">Effective Date: May 08, 2026</p>
          </div>

          <section className="space-y-4 text-white/80 text-base leading-relaxed">
            <p>
              This Privacy Policy describes how AdSmash ("AdSmash", "we", "us", "our") collects, uses, and protects
              information when you use our website, browser extensions, and paid subscription services.
            </p>
            <p>
              We built AdSmash to block ads and trackers while minimizing data collection. This policy explains what
              we collect, why we collect it, who we share it with, and what controls you have.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-3">Who is responsible for your information?</h2>
            <div className="space-y-3 text-white/80">
              <p>
                <strong>Controller:</strong> AdSmash, Inc., 1200 Market Street, Suite 230, San Francisco, CA 94102,
                USA. Contact: <a href="mailto:support@adsmash.com" className="text-primary-300">support@adsmash.com</a>
              </p>
              <p>
                <strong>EU representative:</strong> Reuschlaw Legal Consultants, Mozartstrasse 16, 12247 Berlin,
                Germany (Attn: Carlo Piltz).
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-3">What counts as personal information?</h2>
            <p className="text-white/80">
              "Personal information" means any detail that identifies or can be linked to an individual, such as your
              name, email address, billing information, IP address, or account identifiers. Aggregated or anonymized
              data that cannot be tied back to you is not considered personal information.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold text-white mb-3">Information AdSmash collects</h2>

            <div className="space-y-2 text-white/80">
              <h3 className="text-lg font-semibold text-white">Account and subscription data</h3>
              <p>
                If you create an account or subscribe, we collect details such as name, email, account ID, subscription
                status, and billing profile metadata required to deliver your plan.
              </p>
            </div>

            <div className="space-y-2 text-white/80">
              <h3 className="text-lg font-semibold text-white">Payments and transactions</h3>
              <p>
                Payments are processed by providers such as Stripe and PayPal. We may receive transaction metadata
                (for example, payment status, timestamps, and limited billing details), but we do not store full card
                numbers on AdSmash systems. Payment providers process data under their own terms and policies.
              </p>
            </div>

            <div className="space-y-2 text-white/80">
              <h3 className="text-lg font-semibold text-white">Extension operational data</h3>
              <p>
                To run extension features, we may process technical and configuration data such as extension version,
                settings state, trusted-site preferences, anonymous installation identifiers, and sync timestamps.
                Some settings are stored locally in browser storage on your device.
              </p>
            </div>

            <div className="space-y-2 text-white/80">
              <h3 className="text-lg font-semibold text-white">Ad issue reports and support requests</h3>
              <p>
                If you submit an ad issue report or contact support, we collect the information you provide, which can
                include your email, website URL/domain, selected CSS selector data, message text, and any attachments
                (such as screenshots) you choose to share.
              </p>
            </div>

            <div className="space-y-2 text-white/80">
              <h3 className="text-lg font-semibold text-white">Security and diagnostics</h3>
              <p>
                We process limited logs and diagnostics (such as request metadata, error events, and abuse signals)
                to protect our services, prevent misuse, and troubleshoot issues.
              </p>
            </div>

            <div className="space-y-2 text-white/80">
              <h3 className="text-lg font-semibold text-white">What we do not sell</h3>
              <p>
                We do not sell your personal information. We do not use your data for third-party ad targeting.
                We do not share raw personal data with data brokers.
              </p>
            </div>
          </section>

          <section className="space-y-3 text-white/80">
            <h2 className="text-2xl font-semibold text-white mb-3">How we use information</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>Provide, operate, and improve AdSmash services and extension features.</li>
              <li>Authenticate users and manage subscriptions, billing, and account access.</li>
              <li>Maintain sync and settings continuity across signed-in devices.</li>
              <li>Investigate ad issue reports and improve blocking quality.</li>
              <li>Prevent fraud, abuse, and security incidents.</li>
              <li>Comply with legal obligations and enforce our terms.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-3">Data sharing</h2>
            <p className="text-white/80 mb-3">
              We share data only as needed to run our services, including with:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-white/80">
              <li>Payment processors (for transaction handling).</li>
              <li>Infrastructure and hosting providers (for app delivery and storage).</li>
              <li>Support and communication tools (for customer service workflows).</li>
              <li>Legal authorities when required by applicable law.</li>
            </ul>
            <p className="text-white/80 mt-3">
              These parties are permitted to use data only for authorized service purposes.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-3">Data retention</h2>
            <div className="space-y-3 text-white/80">
              <p>
                We keep data only as long as needed for the purposes described in this policy, including legal,
                accounting, security, and operational obligations.
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Account and billing records: retained while account is active and for required legal periods.</li>
                <li>Operational logs: retained for limited periods, then deleted or de-identified.</li>
                <li>Support and ad issue reports: retained for triage history and product quality work.</li>
                <li>Locally stored browser settings: controlled by you and removable from extension/browser settings.</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-3">Security safeguards</h2>
            <p className="text-white/80">
              We use administrative, technical, and physical safeguards designed to protect personal information,
              including encrypted transport, access controls, and monitoring. No method of transmission or storage
              is completely secure, but we continuously work to improve protection.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-3">Your privacy rights</h2>
            <p className="text-white/80 mb-3">
              Depending on your location, you may have rights to access, correct, delete, restrict, or export your
              personal data, and to object to certain processing activities.
            </p>
            <p className="text-white/80">
              To submit a request, email <a href="mailto:privacy@adsmash.com" className="text-primary-300">privacy@adsmash.com</a>.
              We may verify your identity before fulfilling your request.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-3">International transfers</h2>
            <p className="text-white/80">
              AdSmash operates globally. Your information may be processed in countries other than your own, where
              data protection laws may differ. We use appropriate safeguards for international transfers where required.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-3">Children's privacy</h2>
            <p className="text-white/80">
              AdSmash is not directed to children under 13, and we do not knowingly collect personal information from
              children under 13. If you believe a child has provided personal data, contact us to request deletion.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-3">Changes to this policy</h2>
            <p className="text-white/80">
              We may update this policy from time to time. When we do, we will update the "Effective Date" and, if
              required, provide additional notice.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-3">Contact</h2>
            <p className="text-white/80">
              Email <a href="mailto:support@adsmash.com" className="text-primary-300">support@adsmash.com</a> or mail us at AdSmash, Inc.,
              1200 Market Street, Suite 230, San Francisco, CA 94102 USA.
            </p>
          </section>

          <section className="space-y-3 text-white/80">
            <h2 className="text-2xl font-semibold text-white mb-3">Chrome Web Store policy disclosure</h2>
            <p>
              The use of information received from Google APIs will adhere to the Chrome Web Store User Data Policy,
              including the Limited Use requirements.
            </p>
          </section>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Privacy;
